import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Camera, ShieldCheck, Truck, MapPin } from "lucide-react";
import { CONTACT } from "@/lib/contact";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/89f2f8d9-74f4-4335-b690-b9a815d801c5/images/29a394c6ebac1293f3df48f8711b0e12b2dbb9b14c818612a289626a5e4e2fbe.png";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="bg-white pt-28 md:pt-36 pb-16 md:pb-24 border-b border-[#E5DDD0]"
    >
      <div className="px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-6"
          >
            <span className="brand-gold-divider" />
            <MapPin className="w-3.5 h-3.5 text-[#C9A24C]" />
            Stage 01 · Demolition &amp; Strip-Out · London
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="hero-headline"
            className="font-black tracking-tighter leading-[0.95] text-[#2B2420] text-5xl sm:text-6xl md:text-7xl lg:text-[88px]"
            style={{ fontFamily: "Cabinet Grotesk" }}
          >
            Demolition &amp; <br />
            Strip-Out <br />
            <span className="text-[#B8732C]">in London.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            data-testid="hero-subheadline"
            className="mt-8 max-w-xl text-base md:text-lg text-[#4D4037] leading-relaxed"
          >
            The first stage of every refurbishment — clean, fast and fully
            insured. We can continue all works through to handover.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-cta-quote"
              className="group inline-flex items-center justify-center gap-2 bg-[#B8732C] hover:bg-[#9A5D1E] text-white px-7 py-5 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
            >
              Request a Free Quote
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#quote"
              data-testid="hero-cta-photos"
              className="inline-flex items-center justify-center gap-2 border border-[#2B2420] hover:bg-[#2B2420] hover:text-white text-[#2B2420] px-7 py-5 text-sm font-bold uppercase tracking-[0.15em] transition-all"
            >
              <Camera className="w-4 h-4" />
              Send Photos for Estimate
            </a>
            <a
              href={`tel:${CONTACT.primary.tel}`}
              data-testid="hero-cta-call"
              className="inline-flex items-center justify-center gap-2 text-[#2B2420] hover:text-[#B8732C] px-3 py-5 text-sm font-semibold"
            >
              <Phone className="w-4 h-4" />
              {CONTACT.primary.display}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs md:text-sm text-[#4D4037]"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#B8732C]" />
              Fully Insured
            </div>
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4 text-[#B8732C]" />
              Licensed Waste Carrier
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#C9A24C] font-bold">★</span>
              15+ Years on London Sites
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="lg:col-span-5"
        >
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#F5EFE5]">
            <img
              src={HERO_IMG}
              alt="Commercial strip-out site in London"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 bg-white px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#2B2420] shadow-sm">
              Strip-out in progress
            </div>
            <div className="absolute bottom-4 right-4 bg-[#B8732C] text-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24C]">Stage 01</div>
              <div className="font-black tracking-tight" style={{ fontFamily: "Cabinet Grotesk" }}>
                Then we build.
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
